import {ConfigProvider, Skeleton, Spin} from 'antd';
import {useState} from 'react';
import {AiFillProduct} from 'react-icons/ai';
import {FaUserPlus} from 'react-icons/fa';
import {IoIosWarning} from 'react-icons/io';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';

import {RQGetAllBillsAPI, RqAccountantLookupAll, RqEmployeeLookupAll, RqGetAllFinalProducts, RqGetAllProducts} from '../../../reactQuery/apis';
import {RqGetAllInventory} from '../../../reactQuery/apis/rqInventoryAPI';
import {selectAuth} from '../../../redux/selectors';

import {DashboardScreen} from './DashboardScreen';
import {FinalProductLowStock} from './LowStockModal';
import LowStockModal from './LowStockModal/LowStockModal';

const MainDashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFinalModalOpen, setIsFinalModalOpen] = useState(false);
  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const {
    data: allEmployees = [],
    isLoading: isEmployeesLoading,
  } = RqEmployeeLookupAll(token);

  const {
    data: allAccountants = [],
    isLoading: isAccountantsLoading,
  } = RqAccountantLookupAll(token);

  const {
    data: allBills = [],
    isLoading: isBillsLoading,
  } = RQGetAllBillsAPI(token);
  
  const LOW_STOCK_LIMIT = 5;
  const {
    data: allProducts = [],
    isLoading: isProductsLoading,
  } = RqGetAllProducts(token);

  const {
    data: allFinalProducts = [],
    isLoading: isFinalProductsLoading,
  } = RqGetAllFinalProducts(token);

  const {data: inventoryList = []} = RqGetAllInventory(token);

  const lowStockItems = allProducts
    .filter(product => Number(product?.quantity ?? 0) <= LOW_STOCK_LIMIT)
    .map(product => ({
      id: product.id,
      productName: product.productName,
      brand: product.brand,
      category: product.category,
      color: product.color,
      size: product.size,
      quantity: Number(product.quantity ?? 0),
      sellingPrice: product.sellingPrice ?? 0,
      status: product.status,
    }));
  // console.log('list of the invenery', inventoryList);

  const lowStockProducts = allProducts.filter(
    product => Number(product?.quantity ?? 0) < LOW_STOCK_LIMIT,
  );

  // Add final product low stock items (alongside existing lowStockItems)
  const lowStockFinalItems = allFinalProducts
    .filter(product => Number(product?.quantity ?? 0) <= LOW_STOCK_LIMIT)
    .map(product => ({
      id: product.id,
      productName: product.productName,
      brand: product.brand,
      category: product.category,
      quantity: Number(product.quantity ?? 0),
      status: product.status,
      hsnCode: product.hsnCode,
    }));

  const employeeCount = allEmployees.length;
  const customerCount = allAccountants.length;
  const productCount = allProducts.length;
  const FinalProductCount = allFinalProducts.length;
  const totalProducts = inventoryList.length;

  if (isEmployeesLoading || isAccountantsLoading || isProductsLoading || isBillsLoading || isFinalProductsLoading) {
    return (
      <div className={'flex justify-center items-center h-screen bg-primary-100'}>
        <Spin size={'large'} />
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Employees',
      count: employeeCount,
      icon: <FaUserPlus className={'md:text-4xl text-3xl'} />,
      to: '/admin/employee',
      loading: isEmployeesLoading,
    },
    {
      label: 'Total Customers',
      count: customerCount,
      icon: <FaUserPlus className={'md:text-4xl text-3xl'} />,
      to: '/admin/employee',
      loading: isAccountantsLoading,
    },

    {
      label: 'Total Raw Products',
      count: productCount,
      icon: <AiFillProduct className={'md:text-4xl text-3xl'} />,
      to: '/admin/products',
      loading: isProductsLoading,
    },
    {
      label: 'Total Final Products',
      count: FinalProductCount,
      icon: <AiFillProduct className={'md:text-4xl text-3xl'} />,
      to: '/admin/products',
      loading: isFinalProductsLoading,
    },
    {
      label: 'Total Inventory',
      count: totalProducts,
      icon: <AiFillProduct className={'md:text-4xl text-3xl'} />,
      to: '/admin/inventory',
    },
  ];

  return (
    <ConfigProvider theme={{token: {colorPrimary: '#ffffff'}}}>
      <p className={'header  mb-4'}>Dashboard</p>

      <div className={'p-0 md:p-8 justify-center items-center w-full text-black min-h-[80vh]'}>

        {/* ── Stat Cards ── */}
        <div  className={'flex justify-center items-center text-center'}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6 mb-12'}>

            {statCards.map(({label, count, icon, to, loading}, index) => {
              const gradients = [
                'from-primary-900 to-primary-600',
                'from-primary-900 to-primary-600',
                'from-primary-900 to-primary-600',
                'from-primary-900 to-primary-600',
                'from-primary-900 to-primary-600',
              ];

              return (
                <Link key={label} to={to}>
                  <div
                    className={`
            relative overflow-hidden
            rounded-lg
            bg-gradient-to-br ${gradients[index]}
            p-6
            h-40
            shadow-xl
            transition-all
            duration-300
            hover:scale-105
            hover:-translate-y-2
            hover:shadow-2xl
            cursor-pointer
          `}
                  >
                    {/* Background Circle */}
                    <div className={'absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10'}></div>
                    <div className={'absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-white/5'}></div>

                    <div className={'relative z-10 flex flex-col justify-between h-full'}>
                      <div className={'flex justify-between items-start'}>
                        <div className={'bg-white/20 backdrop-blur-md p-4 mt-4 rounded-full text-white text-2xl'}>
                          {icon}
                        </div>
                        <div className={'w-full flex flex-col justify-center text-center items-center'}>
                          <p className={'text-white/90 text-base font-medium uppercase tracking-wider'}>
                            {label}
                          </p>

                          <h2 className={'text-5xl font-bold text-white mt-3'}>
                            {loading ? (
                              <Skeleton.Input active size={'small'} style={{width: 60}} />
                            ) : (
                              count
                            )}
                          </h2>
                        </div>
                      
                      </div>

                      <div className={'text-white/70 flex ml-4 w-full justify-center text-center items-center text-sm font-medium'}>
              View Details →
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className={'flex justify-center'}>
          <div className={'grid grid-cols-1 md:grid-cols-2 gap-6'}>
            {/* Raw Low Stock Card */}
            <button
              type={'button'}
              onClick={() => setIsModalOpen(true)}
              className={'relative overflow-hidden rounded-md bg-gradient-to-br from-red-600 via-rose-500 to-orange-500 p-6 h-40 shadow-xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 hover:shadow-2xl cursor-pointer w-full md:w-[320px] text-left border-none outline-none'}
            >
              <div className={'absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10'}></div>
              <div className={'absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-white/5'}></div>

              <div className={'relative z-10 flex flex-col justify-between h-full'}>
                <div className={'flex justify-between items-start'}>
                  <div className={'bg-white/20 mr-4 backdrop-blur-md p-4 mt-4 rounded-full text-white text-2xl'}>
                    <IoIosWarning className={'md:text-4xl text-3xl'} />
                  </div>

                  <div className={'w-full flex flex-col justify-center text-center items-center'}>
                    <p className={'text-white/90 text-base font-medium uppercase tracking-wider'}>
              Raw Products
                    </p>

                    <h2 className={'text-5xl font-bold text-white mt-3'}>
                      {isProductsLoading ? (
                        <Skeleton.Input active size={'small'} style={{width: 60}} />
                      ) : (
                        lowStockItems.length
                      )}
                    </h2>
                  </div>
                </div>

                <div className={'flex items-center justify-center gap-2 text-white/80 font-medium'}>
                  <span className={'h-2 w-2 rounded-full bg-red-300 animate-pulse'}></span>
          Check Low in Stock
                </div>
              </div>
            </button>

            {/* Final Low Stock Card */}
            <button
              type={'button'}
              onClick={() => setIsFinalModalOpen(true)}
              className={'relative overflow-hidden rounded-md bg-gradient-to-br from-red-600 via-rose-500 to-orange-500 p-6 h-40 shadow-xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 hover:shadow-2xl cursor-pointer w-full md:w-[320px] text-left border-none outline-none'}
            >
              <div className={'absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10'}></div>
              <div className={'absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-white/5'}></div>

              <div className={'relative z-10 flex flex-col justify-between h-full'}>
                <div className={'flex justify-between items-start'}>
                  <div className={'bg-white/20 mr-4 backdrop-blur-md p-4 mt-4 rounded-full text-white text-2xl'}>
                    <IoIosWarning className={'md:text-4xl text-3xl'} />
                  </div>

                  <div className={'w-full flex flex-col justify-center text-center items-center'}>
                    <p className={'text-white/90 text-base font-medium uppercase tracking-wider'}>
              Final Products
                    </p>

                    <h2 className={'text-5xl font-bold text-white mt-3'}>
                      {isFinalProductsLoading ? (
                        <Skeleton.Input active size={'small'} style={{width: 60}} />
                      ) : (
                        lowStockFinalItems.length
                      )}
                    </h2>
                  </div>
                </div>

                <div className={'flex items-center justify-center gap-2 text-white/80 font-medium'}>
                  <span className={'h-2 w-2 rounded-full bg-red-300 animate-pulse'}></span>
          Check Low in Stock
                </div>
              </div>
            </button>
          </div>
        </div>
                  
        {auth?.user?.role === 'ADMIN' && <DashboardScreen />}
      </div>

      {/* LowStockModal — was commented out, now restored */}
      <LowStockModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        lowStockProducts={lowStockProducts}
        isLoading={isProductsLoading}
      />
      <FinalProductLowStock
        isModalOpen={isFinalModalOpen}
        setIsModalOpen={setIsFinalModalOpen}
        lowStockFinalProducts={lowStockFinalItems}
        isLoading={isFinalProductsLoading}
      />
    </ConfigProvider>
  );
};

export default MainDashboard;
